import { useMemo } from 'react'
import { useI18n } from './LanguageContext'
import { basePlaces } from '../data/base/places'
import { baseAreas } from '../data/base/areas'
import { baseTimeline } from '../data/base/timeline'
import { basePhotos } from '../data/photos'
import { describeFromOrigin } from '../utils/geo'

// Merges the structural place list (ids, coordinates, category, photo) with
// the active language's text (name, summary, details).
export function usePlaces() {
  const { t } = useI18n()
  return useMemo(() => {
    const byId = Object.fromEntries(t.places.items.map((item) => [item.id, item]))
    const places = basePlaces.map((place) => ({ ...place, ...byId[place.id] }))
    return {
      places,
      categories: t.places.categories,
      placeFilters: t.places.filters,
      getPlace: (id) => places.find((place) => place.id === id),
    }
  }, [t])
}

export function usePhotos() {
  const { t } = useI18n()
  return useMemo(() => {
    const photos = Object.fromEntries(
      Object.entries(basePhotos).map(([id, base]) => [id, { ...base, ...(t.photos[id] ?? {}) }]),
    )
    return {
      photos,
      photoList: Object.values(photos),
      photosFor: (ids) => ids.map((id) => photos[id]),
      galleryFilters: t.gallery.filters,
    }
  }, [t])
}

export function useAreas() {
  const { t } = useI18n()
  return useMemo(() => {
    const byId = Object.fromEntries(t.explore.areas.map((area) => [area.id, area]))
    return baseAreas.map((area) => ({ ...area, ...byId[area.id] }))
  }, [t])
}

export function useTimeline() {
  const { t } = useI18n()
  return useMemo(() => {
    const byId = Object.fromEntries(t.timeline.items.map((item) => [item.id, item]))
    const items = baseTimeline
      .map((item) => ({ ...item, ...byId[item.id] }))
      .sort((a, b) => a.sort - b.sort)
    return { items, filters: t.timeline.filters }
  }, [t])
}

// Straight-line distance + compass direction from Nanovitsa, using the
// compass abbreviations of the active language.
export function useDistance() {
  const { t } = useI18n()
  return (place) => describeFromOrigin(place, t.common.directions)
}
